type Listener<Args extends any[]> = (...args: Args) => any;

/**
 * Basically just an array of functions that are called whenever `emit` is run.
 *
 * To change the listener signatures, provide an generic array of args, e.g.
 * `myEmitter = new EventEmitter<[botName: string, botHP: number, isPlayer: boolean, etc]>();`
 *
 * **`emit` is synchronous:** listener return values (including `Promise`s) are ignored. For work that must finish
 * before the caller continues (ordered async hooks, flush-then-UI), use an explicit await loop or a dedicated helper
 * until/unless this type gains an async variant (e.g. `emitAwaitingListeners` with defined error and ordering rules).
 */
export class EventEmitter<Args extends any[] = []> {
  protected listeners: Listener<Args>[] = [];

  /**
   * Add functions to run when `emit` is called.
   * @param listener - a function to add.
   */
  public add(...listeners: Listener<Args>[]): void {
    this.listeners.push(...listeners);
  }

  /**
   * Removes a previously added listener.
   * @param listener - a function previously provided to `add`.
   */
  public remove(listener: Listener<Args>): void {
    this.listeners = this.listeners.filter((x) => x !== listener);
  }

  /**
   * Run all the listener functions added via {@link add}. Does not await promises returned by listeners.
   * @param args - params provided to every listener function.
   */
  public emit(...args: Args) {
    this.listeners.forEach((x) => x(...args));
  }

  /**
   * Like `on` but the function is removed after the first time it's run.
   * @param listener - a function to add.
   */
  public once(listener: Listener<Args>) {
    const removeAndCall = (...args: Args) => {
      this.remove(removeAndCall);
      listener(...args);
    };

    this.add(removeAndCall);
  }

  /**
   * Remove all listeners. Never forget to call on cleanup/destroy.
   */
  public removeAll(): void {
    this.listeners = [];
  }
}
